import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");

  const [status, setStatus] = useState("checking"); // checking | paid | pending | failed
  const [detail, setDetail] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (cancelled) return;
        setDetail(data);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("failed"); return; }
        setStatus("pending");
        if (attempts >= 15) return; // ~30s
        setTimeout(() => setAttempts((a) => a + 1), 2000);
      } catch (e) {
        if (cancelled) return;
        setStatus("failed"); setDetail({ error: formatApiError(e) });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-black text-zinc-100 grid-bg flex flex-col">
      <div className="border-b border-zinc-900 bg-black/80 backdrop-blur px-6 py-3">
        <Logo size={28} />
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md border border-zinc-800 bg-zinc-950 p-8" data-testid="payment-result">
          {status === "checking" || status === "pending" ? (
            <div className="text-center space-y-4">
              <Loader2 size={40} className="mx-auto text-cyan-400 animate-spin" />
              <div className="font-display text-2xl">Confirming payment...</div>
              <div className="mono text-[11px] text-zinc-500">
                {status === "pending" ? "Waiting for Stripe webhook..." : "Contacting Stripe..."}
              </div>
            </div>
          ) : status === "paid" ? (
            <div className="text-center space-y-4">
              <CheckCircle2 size={44} className="mx-auto text-emerald-400" />
              <div className="font-display text-2xl">Payment successful</div>
              <div className="mono text-[11px] text-zinc-400">
                Tenant subscription extended and upgraded to <span className="text-emerald-400">PRO</span>.
              </div>
              <Button
                data-testid="payment-continue"
                onClick={() => navigate("/super")}
                className="rounded-none bg-cyan-500 hover:bg-cyan-400 text-black uppercase tracking-widest text-xs"
              >
                ▸ Back to Super Admin
              </Button>
            </div>
          ) : (
            <div className="text-center space-y-4">
              <XCircle size={44} className="mx-auto text-red-400" />
              <div className="font-display text-2xl">Payment not confirmed</div>
              <div className="mono text-[11px] text-zinc-500">
                {detail?.error || "The payment could not be verified yet. Check the Payments tab or try again."}
              </div>
              <Button
                onClick={() => navigate("/super")}
                variant="ghost"
                className="rounded-none border border-zinc-700 text-zinc-300 uppercase tracking-widest text-xs"
              >
                ▸ Back to Super Admin
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
